import { ReactElement, useMemo } from 'react'
import { MenuItem, Stack, TextField } from '@mui/material'
import { SortButton, SortButtonGroup } from '@ui2/forms'
import { MaterialSymbol } from '@ui2/icons'
import { DividerHeading } from '@ui2/structure'
import type { PlanTableSorting, TableData } from 'src/domain/planTables/types'

const NO_SORTING_VALUE = ''

interface Props {
  /** The table content the plan draws, whose columns can be sorted by. */
  table: TableData
  sorting: PlanTableSorting | null | undefined
  onChange: (sorting: PlanTableSorting | null) => void
}

const PlanTableSortingFields = ({ table, sorting, onChange }: Props): ReactElement => {
  const columnOptions = useMemo(
    () =>
      table.columns.map((column, index) => {
        const headerText = table.headerRow?.cells
          .find(cell => cell.columnKey === column.key)
          ?.value.join(' ')
          .trim()
        return { value: column.key, label: headerText || column.header || `Spalte ${index + 1}` }
      }),
    [table],
  )

  // A column that no longer exists (e.g. hidden by a template option) sorts nothing in the plan.
  const columnKey = columnOptions.some(option => option.value === sorting?.columnKey)
    ? sorting?.columnKey
    : NO_SORTING_VALUE
  const direction = sorting?.direction ?? 'asc'
  const isSorted = columnKey !== NO_SORTING_VALUE
  const directionLabel = direction === 'asc' ? 'Aufsteigend' : 'Absteigend'

  const handleColumnChange = (nextColumnKey: string): void => {
    onChange(nextColumnKey === NO_SORTING_VALUE ? null : { columnKey: nextColumnKey, direction })
  }

  const toggleDirection = (): void => {
    if (!sorting) return
    onChange({ ...sorting, direction: sorting.direction === 'asc' ? 'desc' : 'asc' })
  }

  return (
    <Stack spacing={0.75} data-cy="plan-table-sorting">
      <DividerHeading spacingTop={0}>Sortierung</DividerHeading>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          select
          label="Spalte"
          size="small"
          value={columnKey}
          onChange={event => handleColumnChange(event.target.value)}
          fullWidth
          data-cy="plan-table-sorting-column"
        >
          <MenuItem value={NO_SORTING_VALUE}>Keine Sortierung</MenuItem>
          {columnOptions.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-cy={`plan-table-sorting-column-${option.value}`}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <SortButtonGroup height={40}>
          <SortButton
            tooltip={directionLabel}
            active={isSorted}
            onClick={toggleDirection}
            direction={direction}
            disabled={!isSorted}
            data-cy="plan-table-sorting-direction"
          >
            <MaterialSymbol icon="trending_down" size={16} />
          </SortButton>
        </SortButtonGroup>
      </Stack>
    </Stack>
  )
}

export default PlanTableSortingFields
